import { useMemo, useState } from "react";
import { ChevronRight, Star, Zap } from "lucide-react";
import { useRunAlerts } from "@/api/runs";
import type { AlertDelivery, ScenarioAlertPreview } from "@/api/types";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

interface Props {
  runId: string;
  deliveries?: Record<string, AlertDelivery>;
}

const PRIORITY_TONE: Record<string, string> = {
  critical: "text-red-500 dark:text-red-400 border-red-500/40 bg-red-500/[0.06]",
  warning: "text-amber-600 dark:text-amber-400 border-amber-500/40 bg-amber-500/[0.06]",
  info: "text-sky-600 dark:text-sky-300 border-sky-500/40 bg-sky-500/[0.06]",
};

const PRIORITY_RANK: Record<string, number> = { critical: 0, warning: 1, info: 2 };

interface AlertGroup {
  alertname: string;
  alerts: ScenarioAlertPreview[];
  hasPrimary: boolean;
  // The highest-priority value seen across the group.
  priority: string;
}

function groupByAlertname(alerts: ScenarioAlertPreview[]): AlertGroup[] {
  const groups = new Map<string, AlertGroup>();
  for (const a of alerts) {
    const g = groups.get(a.alertname);
    if (g) {
      g.alerts.push(a);
      g.hasPrimary = g.hasPrimary || a.primary;
      if ((PRIORITY_RANK[a.priority] ?? 99) < (PRIORITY_RANK[g.priority] ?? 99)) {
        g.priority = a.priority;
      }
    } else {
      groups.set(a.alertname, {
        alertname: a.alertname,
        alerts: [a],
        hasPrimary: a.primary,
        priority: a.priority,
      });
    }
  }
  // Stable ordering: primary group first, then by priority, then by earliest event_at.
  return [...groups.values()].sort((a, b) => {
    if (a.hasPrimary !== b.hasPrimary) return a.hasPrimary ? -1 : 1;
    const dp = (PRIORITY_RANK[a.priority] ?? 99) - (PRIORITY_RANK[b.priority] ?? 99);
    if (dp !== 0) return dp;
    return a.alerts[0].event_at.localeCompare(b.alerts[0].event_at);
  });
}

export function RunAlertsPanel({ runId, deliveries }: Props) {
  const alerts = useRunAlerts(runId);
  const data = alerts.data ?? [];
  // Hooks must run on every render — derive before any conditional return.
  const groups = useMemo(() => groupByAlertname(data), [data]);
  const primary = data.find((a) => a.primary) ?? null;
  const deliveryMap = deliveries ?? {};
  const deliveryMapPresent = deliveries !== undefined;

  if (alerts.isLoading) {
    return (
      <Card className="p-4">
        <p className="text-xs text-muted-foreground">Loading alerts…</p>
      </Card>
    );
  }
  if (alerts.isError || !alerts.data) {
    return (
      <Card className="p-4">
        <p className="text-xs text-muted-foreground">Couldn't load alerts for this run.</p>
      </Card>
    );
  }
  if (alerts.data.length === 0) {
    return (
      <Card className="p-4">
        <p className="text-xs text-muted-foreground">This run fires no alerts.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <header className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-foreground inline-flex items-center gap-2">
            <Zap size={14} className="text-primary" aria-hidden />
            Alerts fired ({data.length})
          </h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            All alerts hit the NeuBird agent as a burst.{" "}
            {primary
              ? "If grouping misses some, investigate the primary alert below — it walks the cleanest path to root cause."
              : "No primary alert is defined for this scenario."}
          </p>
        </div>
      </header>

      {primary && <PrimaryCallout alert={primary} />}

      <ul className="space-y-1.5">
        {groups.map((g) => (
          <AlertGroupRow
            key={g.alertname}
            group={g}
            deliveryMap={deliveryMap}
            deliveryMapPresent={deliveryMapPresent}
          />
        ))}
      </ul>
    </div>
  );
}

function PrimaryCallout({ alert }: { alert: ScenarioAlertPreview }) {
  return (
    <div className="rounded-lg border border-primary/40 bg-primary/[0.05] p-3 flex items-start gap-3">
      <Star
        size={16}
        className="mt-0.5 text-primary fill-primary flex-shrink-0"
        aria-label="primary"
      />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2 flex-wrap mb-1">
          <span className="text-[10px] uppercase tracking-wide font-semibold text-primary">
            Primary alert
          </span>
          <span className="text-[11px] text-muted-foreground">
            pick this one to launch the investigation
          </span>
        </div>
        <div className="text-sm font-medium text-foreground">{alert.title}</div>
        <div className="text-[11px] text-muted-foreground font-mono mt-0.5 flex items-center gap-1.5 flex-wrap">
          <span>{alert.alertname}</span>
          <span aria-hidden>·</span>
          <span>{alert.service}</span>
          <span aria-hidden>·</span>
          <span>{alert.event_at}</span>
          {alert.caused_by && (
            <>
              <span aria-hidden>·</span>
              <span>caused by {alert.caused_by}</span>
            </>
          )}
        </div>
        {alert.primary_rationale && (
          <p className="text-xs text-muted-foreground mt-2 leading-snug">
            {alert.primary_rationale}
          </p>
        )}
      </div>
    </div>
  );
}

function AlertGroupRow({
  group,
  deliveryMap,
  deliveryMapPresent,
}: {
  group: AlertGroup;
  deliveryMap: Record<string, AlertDelivery>;
  deliveryMapPresent: boolean;
}) {
  const [open, setOpen] = useState(false);
  const count = group.alerts.length;
  const first = group.alerts[0];
  const services = Array.from(new Set(group.alerts.map((a) => a.service)));
  const serviceLabel =
    services.length === 1
      ? services[0]
      : `${services.length} services`;
  const expandable = count > 1 || services.length > 1;

  return (
    <li
      className={cn(
        "rounded-md border bg-card overflow-hidden",
        group.hasPrimary ? "border-primary/30" : "border-border",
      )}
    >
      {/* Row: expandable trigger + delivery pills sit side-by-side, pills outside the button */}
      <div className="flex items-start">
        <button
          type="button"
          onClick={() => expandable && setOpen((v) => !v)}
          disabled={!expandable}
          className={cn(
            "flex-1 min-w-0 px-3 py-2 flex items-start gap-2 text-left",
            expandable && "hover:bg-muted/30 transition-colors cursor-pointer",
          )}
          aria-expanded={expandable ? open : undefined}
        >
          <span className="mt-0.5 flex-shrink-0 w-3">
            {expandable ? (
              <ChevronRight
                size={13}
                className={cn(
                  "text-muted-foreground transition-transform",
                  open && "rotate-90",
                )}
                aria-hidden
              />
            ) : (
              <span className="inline-block h-3 w-3 rounded-full border border-muted-foreground/40" />
            )}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="text-sm font-mono text-foreground">{group.alertname}</span>
              {count > 1 && (
                <span className="inline-flex items-center h-4 px-1.5 rounded text-[10px] font-mono bg-muted text-muted-foreground border border-border">
                  ×{count}
                </span>
              )}
              <span
                className={cn(
                  "inline-flex items-center h-4 px-1.5 rounded text-[10px] font-mono uppercase tracking-wide border",
                  PRIORITY_TONE[group.priority] ?? "border-border text-muted-foreground",
                )}
              >
                {group.priority}
              </span>
              {group.hasPrimary && (
                <span className="inline-flex items-center h-4 px-1.5 rounded text-[10px] font-mono uppercase tracking-wide border border-primary/40 bg-primary/10 text-primary">
                  primary
                </span>
              )}
            </div>
            <div className="text-xs text-muted-foreground mt-0.5 truncate">
              {count === 1 ? first.title : `Firing on ${serviceLabel}`}
            </div>
            <div className="text-[11px] text-muted-foreground font-mono mt-0.5 flex items-center gap-1.5 flex-wrap">
              <span>{serviceLabel}</span>
              <span aria-hidden>·</span>
              <span>
                {count === 1
                  ? first.event_at
                  : `${group.alerts[0].event_at} → ${group.alerts[count - 1].event_at}`}
              </span>
            </div>
          </div>
        </button>
        {/* Delivery pills — one per alert in the group, outside the expandable button */}
        {deliveryMapPresent && (
          <div className="flex flex-col gap-0.5 py-2 pr-3 flex-shrink-0">
            {group.alerts.map((a) => (
              <DeliveryPill
                key={a.external_id}
                delivery={deliveryMap[a.external_id]}
              />
            ))}
          </div>
        )}
      </div>
      {open && expandable && (
        <ul className="border-t border-border bg-muted/20 divide-y divide-border">
          {group.alerts.map((a) => (
            <li key={a.external_id} className="px-3 py-1.5 pl-9">
              <div className="flex items-baseline gap-2 flex-wrap">
                <span className="text-xs text-foreground truncate">{a.title}</span>
                {a.primary && (
                  <span className="inline-flex items-center h-3.5 px-1 rounded text-[9px] font-mono uppercase tracking-wide border border-primary/40 bg-primary/10 text-primary">
                    primary
                  </span>
                )}
                <DeliveryPill
                  delivery={deliveryMap[a.external_id]}
                />
              </div>
              <div className="text-[11px] text-muted-foreground font-mono flex items-center gap-1.5 flex-wrap">
                <span>{a.service}</span>
                <span aria-hidden>·</span>
                <span>{a.event_at}</span>
                {a.caused_by && (
                  <>
                    <span aria-hidden>·</span>
                    <span>caused by {a.caused_by}</span>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

type PillKind = "ok" | "auth" | "client" | "server" | "network" | "unknown";

function pillKindFor(d: AlertDelivery | undefined): PillKind {
  if (!d) return "unknown";
  if (d.http_status && d.http_status >= 200 && d.http_status < 400) return "ok";
  if (d.error_kind === "auth") return "auth";
  if (d.error_kind === "client_error") return "client";
  if (d.error_kind === "server_error") return "server";
  if (d.error_kind === "network") return "network";
  return "unknown";
}

const PILL_CLASS: Record<PillKind, string> = {
  ok: "border-green-500/40 bg-green-500/[0.08] text-green-700 dark:text-green-300",
  auth: "border-red-500/40 bg-red-500/[0.08] text-red-700 dark:text-red-300",
  client: "border-red-500/40 bg-red-500/[0.08] text-red-700 dark:text-red-300",
  server: "border-amber-500/40 bg-amber-500/[0.08] text-amber-700 dark:text-amber-300",
  network: "border-amber-500/40 bg-amber-500/[0.08] text-amber-700 dark:text-amber-300",
  unknown: "border-border bg-muted/40 text-muted-foreground",
};

function DeliveryPill({
  delivery,
}: {
  delivery: AlertDelivery | undefined;
}) {
  const [open, setOpen] = useState(false);
  const kind = pillKindFor(delivery);
  const clickable = kind !== "ok" && !!delivery;

  let label = "unknown";
  if (kind === "ok" && delivery) label = `✓ ${delivery.http_status} · ${delivery.latency_ms}ms`;
  else if (kind === "auth" && delivery) label = `✗ ${delivery.http_status} auth`;
  else if (kind === "client" && delivery) label = `✗ ${delivery.http_status} client_error`;
  else if (kind === "server" && delivery)
    label = `✗ ${delivery.http_status} server · ${delivery.attempts} attempts`;
  else if (kind === "network" && delivery) label = `✗ network · ${delivery.attempts} attempts`;
  else if (kind === "unknown") label = "unknown";

  return (
    <span className="inline-block">
      <button
        type="button"
        disabled={!clickable}
        onClick={() => clickable && setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={clickable ? open : undefined}
        className={cn(
          "inline-flex items-center h-5 px-2 rounded text-[10px] font-mono border",
          PILL_CLASS[kind],
          clickable && "cursor-pointer hover:opacity-90",
        )}
      >
        {label}
      </button>
      {open && delivery && (
        <div className="mt-1 rounded border border-border bg-muted/30 p-2 text-[11px] font-mono">
          <div>attempted_at: {delivery.attempted_at ?? "n/a"}</div>
          <div>http_status: {delivery.http_status ?? "n/a"}</div>
          <div>attempts: {delivery.attempts}</div>
          {delivery.error_snippet && (
            <pre className="mt-1 whitespace-pre-wrap break-all">{delivery.error_snippet}</pre>
          )}
        </div>
      )}
    </span>
  );
}
